import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, X, Search, Filter, MoreVertical, Globe, FilterX } from 'lucide-react';
import { dashboardAPI } from '../services/api';
import OptionsMenu from './OptionsMenu';
import { bdaOptions } from '../utils/bdeOwnership';

const FilterBar = ({
  selectedDate,
  setSelectedDate,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  selectedBDE,
  selectedCountry = 'All',
  setSelectedCountry,
  countryOptions = [],
  selectedLeadSource = 'All',
  setSelectedLeadSource,
  leadSourceOptions = [],
  setSelectedBDE,
  selectedStage,
  setSelectedStage,
  selectedPriority,
  setSelectedPriority,
  selectedInterest,
  setSelectedInterest,
  searchTerm,
  setSearchTerm,
  onRefresh,
  showDateRange = true,
  showSearch = false,
  showStageFilter = false,
  showPriorityFilter = false,
  showInterestFilter = false,
  showBDE = true,
  onExportExcel,
  onExportPDF,
  showExport = false,
  stageOptions = null,
  onAddLead = null
}) => {
  const [bdeUsers, setBdeUsers] = useState([]);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsAnchorRef = useRef(null);
  
  // Fetch BDE users from API
  useEffect(() => {
    const fetchBDEUsers = async () => {
      try {
        const response = await dashboardAPI.getBDEUsers();
        if (response && response.data) {
          setBdeUsers(response.data);
        }
      } catch (error) {
        console.error('Error fetching BDE users:', error);
      }
    };
    fetchBDEUsers();
  }, []);
  
  const clearDateFilters = () => {
    setSelectedDate && setSelectedDate('');
    setDateFrom && setDateFrom('');
    setDateTo && setDateTo('');
  };

  // Every filter this bar owns, in one place, so "Clear All Filters" and the
  // "is anything filtered?" check can never drift apart. `isSet` decides what
  // counts as active: a dropdown is 'All' when it isn't filtering, a text or
  // date box is ''.
  const FILTERS = [
    { value: searchTerm, setter: setSearchTerm, empty: '', shown: showSearch },
    { value: selectedDate, setter: setSelectedDate, empty: '', shown: !showDateRange },
    { value: dateFrom, setter: setDateFrom, empty: '', shown: showDateRange },
    { value: dateTo, setter: setDateTo, empty: '', shown: showDateRange },
    { value: selectedBDE, setter: setSelectedBDE, empty: 'All', shown: showBDE },
    { value: selectedCountry, setter: setSelectedCountry, empty: 'All', shown: !!setSelectedCountry && countryOptions.length > 0 },
    { value: selectedLeadSource, setter: setSelectedLeadSource, empty: 'All', shown: !!setSelectedLeadSource && leadSourceOptions.length > 0 },
    { value: selectedStage, setter: setSelectedStage, empty: 'All', shown: showStageFilter },
    { value: selectedPriority, setter: setSelectedPriority, empty: 'All', shown: showPriorityFilter },
    { value: selectedInterest, setter: setSelectedInterest, empty: 'All', shown: showInterestFilter }
  ];

  const activeFilterCount = FILTERS.filter(f =>
    f.shown && f.setter && (f.value || f.empty) !== f.empty
  ).length;

  // One click puts the whole bar back to "no filters" — previously every
  // dropdown had to be walked back to All by hand.
  const clearAllFilters = () => {
    FILTERS.forEach(f => { if (f.shown && f.setter) f.setter(f.empty); });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-3 mb-4">
      <div className="flex gap-3 items-center flex-wrap">
        {/* Search Input */}
        {showSearch && (
          <div className="flex items-center gap-2">
            <Search size={20} className="text-gray-400" />
            <input
              type="text"
              placeholder="Search company, POC, email, phone, BDA, country…"
              value={searchTerm || ''}
              onChange={(e) => setSearchTerm && setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent min-w-[200px]"
            />
          </div>
        )}
        {showDateRange ? (
          <>
            {/* Date Range Filter */}
            <div className="flex items-center gap-2">
              <Calendar size={20} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-700">From:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                placeholder="Start date"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">To:</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
                placeholder="End date"
              />
            </div>

            {(dateFrom || dateTo) && (
              <button
                onClick={clearDateFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                title="Clear date filters"
              >
                <X size={16} />
                Clear Dates
              </button>
            )}
          </>
        ) : (
          /* Single Date Filter (for backward compatibility) */
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-gray-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
            />
          </div>
        )}
        
        {/* BDE Filter */}
        {showBDE && (
          <div className="flex items-center gap-2">
            <Users size={20} className="text-gray-400" />
            <select
              value={selectedBDE}
              onChange={(e) => setSelectedBDE(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
            >
              <option value="All">All BDEs</option>
              {bdaOptions(bdeUsers).map((bde) => (
                <option key={bde.id} value={bde.name}>
                  {bde.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Country — options come from the leads themselves */}
        {setSelectedCountry && countryOptions.length > 0 && (
          <div className="flex items-center gap-2">
            <Globe size={20} className="text-gray-400" />
            <select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
              title="Filter by country"
            >
              <option value="All">All Countries</option>
              {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {/* Lead source */}
        {setSelectedLeadSource && leadSourceOptions.length > 0 && (
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={selectedLeadSource}
              onChange={(e) => setSelectedLeadSource(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent"
              title="Filter by lead source"
            >
              <option value="All">All Sources</option>
              {leadSourceOptions.map(sVal => <option key={sVal} value={sVal}>{sVal}</option>)}
            </select>
          </div>
        )}

        {/* Stage Filter */}
        {showStageFilter && (
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={selectedStage || 'All'}
              onChange={(e) => setSelectedStage && setSelectedStage(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent min-w-[160px] max-w-[180px]"
            >
              <option value="All">All Stages</option>
              {stageOptions ? (
                stageOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))
              ) : (
                <>
                  <option value="New">New</option>
                  <option value="Contacted">Contacted</option>
                  <option value="Follow-up 1">Follow-up 1</option>
                  <option value="Follow-up 2">Follow-up 2</option>
                  <option value="Proposal">Proposal</option>
                  <option value="Negotiation">Negotiation</option>
                  <option value="Onboarded">Onboarded</option>
                  <option value="Important - But Contact Later">Important - But Contact Later</option>
                  <option value="Deferred">Deferred</option>
                </>
              )}
            </select>
          </div>
        )}

        {/* Priority Filter */}
        {showPriorityFilter && (
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={selectedPriority || 'All'}
              onChange={(e) => setSelectedPriority && setSelectedPriority(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent min-w-[140px] max-w-[160px]"
            >
              <option value="All">All Priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
        )}

        {/* Interest Filter (Pre-Sales) — dropdown, styled like Stage/Priority */}
        {showInterestFilter && (
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={selectedInterest || 'All'}
              onChange={(e) => setSelectedInterest && setSelectedInterest(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-2 focus:ring-2 focus:ring-[#76AB3F] focus:border-transparent min-w-[150px]"
            >
              <option value="All">All Interest</option>
              <option value="Interested">Interested</option>
              <option value="Not Interested">Not Interested</option>
            </select>
          </div>
        )}

        {/* Clear every filter at once. Only appears when something is filtered,
            and says how many, so it never reads as a dead button. */}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearAllFilters}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#76AB3F] hover:text-white hover:bg-[#76AB3F] border border-[#76AB3F] rounded-lg transition-colors"
            title="Clear all filters"
          >
            <FilterX size={16} />
            Clear All Filters ({activeFilterCount})
          </button>
        )}

        {/* Options (three-dot) - rightmost: Export Excel/PDF */}
        <div className="relative ml-auto flex-shrink-0" ref={optionsAnchorRef}>
          <button
            type="button"
            onClick={() => setOptionsOpen((o) => !o)}
            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 border border-gray-200 transition-colors"
            title="Download / Options"
            aria-label="Page options"
          >
            <MoreVertical size={22} />
          </button>
          <OptionsMenu
            anchorRef={optionsAnchorRef}
            isOpen={optionsOpen}
            onClose={() => setOptionsOpen(false)}
            onExportExcel={onExportExcel}
            onExportPDF={onExportPDF}
            showExport={showExport}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
